// AdminBlog.jsx - Complete Admin Page with State Management
import React, { useState, useEffect } from 'react';
import { 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaPlus, 
  FaSave, 
  FaTimes,
  FaUpload,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaImage,
  FaChartBar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSync,
  FaCloudUploadAlt,
  FaDatabase,
  FaCogs
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Firebase imports
import { database, storage } from '../firebase';
import { ref as dbRef, push, get, set, update, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// Firebase Blog Store
export const useBlogStore = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from Firebase on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch blog posts
        const postsRef = dbRef(database, 'Dr/Blog/posts');
        const postsSnapshot = await get(postsRef);
        if (postsSnapshot.exists()) {
          const postsData = postsSnapshot.val();
          const postsArray = Object.keys(postsData).map(key => ({
            id: key,
            ...postsData[key]
          }));
          setBlogPosts(postsArray);
        }

        // Fetch authors
        const authorsRef = dbRef(database, 'Dr/Blog/authors');
        const authorsSnapshot = await get(authorsRef);
        if (authorsSnapshot.exists()) {
          const authorsData = authorsSnapshot.val();
          const authorsArray = Object.keys(authorsData).map(key => ({
            id: key,
            ...authorsData[key]
          }));
          setAuthors(authorsArray);
        } else {
          // Set default authors if none exist
          const defaultAuthors = [
            { id: '1', name: "Dr. Sarah Johnson", role: "Chief Medical Officer", image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" },
            { id: '2', name: "Dr. Michael Chen", role: "Immunology Researcher", image: "https://images.unsplash.com/photo-1537368910025-700350fe46c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" },
            { id: '3', name: "Dr. Emily Watson", role: "Clinical Psychologist", image: "https://images.unsplash.com/photo-1594824434340-7e7dfc37cabb?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" }
          ];
          setAuthors(defaultAuthors);
          // Save default authors to Firebase
          defaultAuthors.forEach(author => {
            set(dbRef(database, `Dr/Blog/authors/${author.id}`), author);
          });
        }

        // Fetch categories
        const categoriesRef = dbRef(database, 'Dr/Blog/categories');
        const categoriesSnapshot = await get(categoriesRef);
        if (categoriesSnapshot.exists()) {
          const categoriesData = categoriesSnapshot.val();
          const categoriesArray = Object.keys(categoriesData).map(key => ({
            id: key,
            ...categoriesData[key]
          }));
          setCategories(categoriesArray);
        } else {
          // Set default categories if none exist
          const defaultCategories = [
            { id: 'medicine', name: 'Medicine', color: 'from-purple-500 to-violet-500' },
            { id: 'wellness', name: 'Wellness', color: 'from-teal-500 to-emerald-500' },
            { id: 'nutrition', name: 'Nutrition', color: 'from-orange-500 to-yellow-500' },
            { id: 'mental', name: 'Mental Health', color: 'from-blue-500 to-cyan-500' },
            { id: 'fitness', name: 'Fitness', color: 'from-red-500 to-pink-500' },
            { id: 'research', name: 'Medical Research', color: 'from-indigo-500 to-blue-500' }
          ];
          setCategories(defaultCategories);
          // Save default categories to Firebase
          defaultCategories.forEach(category => {
            set(dbRef(database, `Dr/Blog/categories/${category.id}`), category);
          });
        }
      } catch (error) {
        console.error('Error fetching data from Firebase:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // CRUD Operations for Blog Posts
  const addBlogPost = async (post) => {
    try {
      // Generate sequential ID based on current count + 1
      const postId = blogPosts.length + 1;
      const newPost = {
        ...post,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        views: 0,
        likes: 0,
        comments: 0,
        shares: 0
      };
      await set(dbRef(database, `Dr/Blog/posts/${postId}`), newPost);
      
      const postWithId = { id: postId, ...newPost };
      setBlogPosts(prev => [...prev, postWithId]);
      return postWithId;
    } catch (error) {
      console.error('Error adding blog post:', error);
      throw error;
    }
  };

  const updateBlogPost = async (id, updatedPost) => {
    try {
      const postRef = dbRef(database, `Dr/Blog/posts/${id}`);
      const postToUpdate = {
        ...updatedPost,
        updatedAt: new Date().toISOString()
      };
      await update(postRef, postToUpdate);
      
      setBlogPosts(prev => prev.map(post => 
        post.id === id ? { ...postToUpdate, id } : post
      ));
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  };

  const deleteBlogPost = async (id) => {
    try {
      await remove(dbRef(database, `Dr/Blog/posts/${id}`));
      setBlogPosts(prev => prev.filter(post => post.id !== id));
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  };

  // CRUD for Authors
  const addAuthor = async (author) => {
    try {
      // Generate sequential ID based on current count + 1
      const authorId = (authors.length + 1).toString();
      const newAuthor = { ...author };
      await set(dbRef(database, `Dr/Blog/authors/${authorId}`), newAuthor);
      
      const authorWithId = { id: authorId, ...newAuthor };
      setAuthors(prev => [...prev, authorWithId]);
      return authorWithId;
    } catch (error) {
      console.error('Error adding author:', error);
      throw error;
    }
  };

  const updateAuthor = async (id, updatedAuthor) => {
    try {
      await update(dbRef(database, `Dr/Blog/authors/${id}`), updatedAuthor);
      setAuthors(prev => prev.map(author => 
        author.id === id ? { ...updatedAuthor, id } : author
      ));
    } catch (error) {
      console.error('Error updating author:', error);
      throw error;
    }
  };

  const deleteAuthor = async (id) => {
    try {
      await remove(dbRef(database, `Dr/Blog/authors/${id}`));
      setAuthors(prev => prev.filter(author => author.id !== id));
    } catch (error) {
      console.error('Error deleting author:', error);
      throw error;
    }
  };

  // CRUD for Categories
  const addCategory = async (category) => {
    try {
      // Generate sequential ID based on current count + 1
      const categoryId = (categories.length + 1).toString();
      const newCategory = { ...category };
      await set(dbRef(database, `Dr/Blog/categories/${categoryId}`), newCategory);
      
      const categoryWithId = { id: categoryId, ...newCategory };
      setCategories(prev => [...prev, categoryWithId]);
      return categoryWithId;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  };

  const updateCategory = async (id, updatedCategory) => {
    try {
      await update(dbRef(database, `Dr/Blog/categories/${id}`), updatedCategory);
      setCategories(prev => prev.map(category => 
        category.id === id ? { ...updatedCategory, id } : category
      ));
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  };

  const deleteCategory = async (id) => {
    try {
      await remove(dbRef(database, `Dr/Blog/categories/${id}`));
      setCategories(prev => prev.filter(category => category.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  // Get statistics
  const getStats = () => {
    return {
      totalPosts: blogPosts.length,
      publishedPosts: blogPosts.filter(post => post.status === 'published').length,
      draftPosts: blogPosts.filter(post => post.status === 'draft').length,
      featuredPosts: blogPosts.filter(post => post.featured).length,
      totalAuthors: authors.length,
      totalCategories: categories.length,
      totalViews: blogPosts.reduce((sum, post) => sum + (post.views || 0), 0),
      totalLikes: blogPosts.reduce((sum, post) => sum + (post.likes || 0), 0)
    };
  };

  // Export data
  const exportData = () => {
    const data = {
      blogPosts,
      authors,
      categories,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    return JSON.stringify(data, null, 2);
  };

  // Import data
  const importData = (data) => {
    try {
      const parsedData = JSON.parse(data);
      if (parsedData.blogPosts) setBlogPosts(parsedData.blogPosts);
      if (parsedData.authors) setAuthors(parsedData.authors);
      if (parsedData.categories) setCategories(parsedData.categories);
      return true;
    } catch (error) {
      return false;
    }
  };

  return {
    // State
    blogPosts,
    authors,
    categories,
    
    // Blog Post Operations
    addBlogPost,
    updateBlogPost,
    deleteBlogPost,
    
    // Author Operations
    addAuthor,
    updateAuthor,
    deleteAuthor,
    
    // Category Operations
    addCategory,
    updateCategory,
    deleteCategory,
    
    // Utility Functions
    getStats,
    exportData,
    importData
  };
};

const AdminBlog = () => {
  // Use blog store
  const blogStore = useBlogStore();
  
  // State for UI
  const [activeTab, setActiveTab] = useState('posts');
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showImportExport, setShowImportExport] = useState(false);
  const [importData, setImportData] = useState('');

  // Form states
  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    authorId: '',
    date: new Date().toISOString().split('T')[0],
    readTime: '',
    category: 'medicine',
    tags: [],
    tagInput: '',
    image: '',
    status: 'draft',
    featured: false
  });

  // Image upload states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Author and Category image upload states
  const [authorImageFile, setAuthorImageFile] = useState(null);
  const [authorImagePreview, setAuthorImagePreview] = useState(null);
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState(null);

  const [authorForm, setAuthorForm] = useState({
    name: '',
    role: '',
    image: '',
    bio: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: 'from-teal-500 to-emerald-500',
    description: ''
  });

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4 }
    }
  };

  // Filter posts based on search and status
  const filteredPosts = blogStore.blogPosts.filter(post => {
    if (searchQuery && !post.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterStatus !== 'all' && post.status !== filterStatus) {
      return false;
    }
    return true;
  });

  // Get statistics
  const stats = blogStore.getStats();

  // Handle Post Operations
  const handleCreatePost = async () => {
    if (!postForm.title || !postForm.excerpt) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setIsUploading(true);
      
      // Create post first to get ID
      const newPost = await blogStore.addBlogPost({
        ...postForm,
        author: blogStore.authors.find(a => a.id == postForm.authorId)?.name || 'Unknown Author',
        authorRole: blogStore.authors.find(a => a.id == postForm.authorId)?.role || ''
      });

      // Upload image if selected
      if (imageFile) {
        const imageUrl = await uploadImage(newPost.id);
        if (imageUrl) {
          await blogStore.updateBlogPost(newPost.id, { ...newPost, image: imageUrl });
        }
      }

      toast.success('Blog post created successfully!');
      resetPostForm();
      setIsCreating(false);
    } catch (error) {
      toast.error('Failed to create blog post');
      console.error('Error creating post:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdatePost = async () => {
    if (!selectedPost) return;

    try {
      setIsUploading(true);
      
      // Upload new image if selected
      let imageUrl = selectedPost.image;
      if (imageFile) {
        const uploadedImageUrl = await uploadImage(selectedPost.id);
        if (uploadedImageUrl) {
          imageUrl = uploadedImageUrl;
        }
      }

      await blogStore.updateBlogPost(selectedPost.id, {
        ...selectedPost,
        image: imageUrl,
        author: blogStore.authors.find(a => a.id == selectedPost.authorId)?.name || 'Unknown Author',
        authorRole: blogStore.authors.find(a => a.id == selectedPost.authorId)?.role || ''
      });

      toast.success('Blog post updated successfully!');
      setSelectedPost(null);
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update blog post');
      console.error('Error updating post:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeletePost = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogStore.deleteBlogPost(id);
        toast.success('Blog post deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete blog post');
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleEditPost = (post) => {
    setSelectedPost({ ...post });
    setIsEditing(true);
  };

  // Handle Author Operations
  const handleCreateAuthor = async () => {
    if (!selectedAuthor || !selectedAuthor.name) {
      toast.error('Author name is required');
      return;
    }

    try {
      // Create author first to get ID
      const newAuthor = await blogStore.addAuthor(selectedAuthor);

      // Upload image if selected
      if (authorImageFile) {
        const imageUrl = await uploadAuthorImage(newAuthor.id);
        if (imageUrl) {
          await blogStore.updateAuthor(newAuthor.id, { ...newAuthor, image: imageUrl });
        }
      }

      toast.success('Author created successfully!');
      setSelectedAuthor(null);
      setAuthorImageFile(null);
      setAuthorImagePreview(null);
    } catch (error) {
      toast.error('Failed to create author');
      console.error('Error creating author:', error);
    }
  };

  const handleUpdateAuthor = async () => {
    if (!selectedAuthor) return;

    try {
      // Upload new image if selected
      let imageUrl = selectedAuthor.image;
      if (authorImageFile) {
        const uploadedImageUrl = await uploadAuthorImage(selectedAuthor.id);
        if (uploadedImageUrl) {
          imageUrl = uploadedImageUrl;
        }
      }

      await blogStore.updateAuthor(selectedAuthor.id, {
        ...selectedAuthor,
        image: imageUrl
      });

      toast.success('Author updated successfully!');
      setSelectedAuthor(null);
      setAuthorImageFile(null);
      setAuthorImagePreview(null);
    } catch (error) {
      toast.error('Failed to update author');
      console.error('Error updating author:', error);
    }
  };

  // Handle Category Operations
  const handleCreateCategory = async () => {
    if (!selectedCategory || !selectedCategory.name) {
      toast.error('Category name is required');
      return;
    }

    try {
      await blogStore.addCategory(selectedCategory);
      toast.success('Category created successfully!');
      setSelectedCategory(null);
    } catch (error) {
      toast.error('Failed to create category');
      console.error('Error creating category:', error);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory) return;

    try {
      await blogStore.updateCategory(selectedCategory.id, selectedCategory);
      toast.success('Category updated successfully!');
      setSelectedCategory(null);
    } catch (error) {
      toast.error('Failed to update category');
      console.error('Error updating category:', error);
    }
  };

  // Form helpers
  const resetPostForm = () => {
    setPostForm({
      title: '',
      excerpt: '',
      content: '',
      authorId: '',
      date: new Date().toISOString().split('T')[0],
      readTime: '',
      category: 'medicine',
      tags: [],
      tagInput: '',
      image: '',
      status: 'draft',
      featured: false
    });
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
  };

  const handleAddTag = () => {
    if (postForm.tagInput.trim() && !postForm.tags.includes(postForm.tagInput.trim())) {
      setPostForm({
        ...postForm,
        tags: [...postForm.tags, postForm.tagInput.trim()],
        tagInput: ''
      });
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setPostForm({
      ...postForm,
      tags: postForm.tags.filter(tag => tag !== tagToRemove)
    });
  };

  // Image upload handlers
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setPostForm(prev => ({ ...prev, image: '' }));
  };

  const uploadImage = async (postId) => {
    if (!imageFile) return existingImageUrl;

    try {
      const imageRef = storageRef(
        storage,
        `Dr/Blog/${postId}/image.jpg`
      );
      await uploadBytes(imageRef, imageFile);
      const imageUrl = await getDownloadURL(imageRef);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  // Author and Category image upload handlers
  const handleAuthorImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setAuthorImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAuthorImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCategoryImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setCategoryImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCategoryImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const uploadAuthorImage = async (authorId) => {
    if (!authorImageFile) return selectedAuthor?.image;

    try {
      const imageRef = storageRef(
        storage,
        `Dr/Blog/authors/${authorId}/image.jpg`
      );
      await uploadBytes(imageRef, authorImageFile);
      const imageUrl = await getDownloadURL(imageRef);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading author image:', error);
      toast.error('Failed to upload author image');
      return null;
    }
  };

  const uploadCategoryImage = async (categoryId) => {
    if (!categoryImageFile) return selectedCategory?.image;

    try {
      const imageRef = storageRef(
        storage,
        `Dr/Blog/categories/${categoryId}/image.jpg`
      );
      await uploadBytes(imageRef, categoryImageFile);
      const imageUrl = await getDownloadURL(imageRef);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading category image:', error);
      toast.error('Failed to upload category image');
      return null;
    }
  };

  const removeAuthorImage = () => {
    setAuthorImageFile(null);
    setAuthorImagePreview(null);
    setSelectedAuthor(prev => ({ ...prev, image: '' }));
  };

  const removeCategoryImage = () => {
    setCategoryImageFile(null);
    setCategoryImagePreview(null);
    setSelectedCategory(prev => ({ ...prev, image: '' }));
  };

  // Export/Import functions
  const handleExport = () => {
    const data = blogStore.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medical-blog-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully!');
  };

  const handleImport = () => {
    if (!importData.trim()) {
      toast.error('Please paste JSON data to import');
      return;
    }

    const success = blogStore.importData(importData);
    if (success) {
      toast.success('Data imported successfully!');
      setImportData('');
      setShowImportExport(false);
    } else {
      toast.error('Invalid JSON data');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-4 md:p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Medical Blog Admin</h1>
            <p className="text-gray-600">Manage all blog content that appears on the main blog page</p>
          </div>
          
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowImportExport(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium flex items-center space-x-2 hover:bg-gray-200 transition-colors"
            >
              <FaDatabase />
              <span>Data Tools</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="px-4 py-2 bg-red-100 text-red-600 rounded-xl font-medium flex items-center space-x-2 hover:bg-red-200 transition-colors"
            >
              <FaSync />
              <span>Reset Data</span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Stats Overview */}
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
      >
        {[
          { label: 'Total Posts', value: stats.totalPosts, color: 'from-blue-500 to-cyan-500', desc: 'All blog articles' },
          { label: 'Published', value: stats.publishedPosts, color: 'from-green-500 to-emerald-500', desc: 'Live on blog' },
          { label: 'Featured', value: stats.featuredPosts, color: 'from-purple-500 to-pink-500', desc: 'Highlighted posts' },
          { label: 'Authors', value: stats.totalAuthors, color: 'from-orange-500 to-yellow-500', desc: 'Contributors' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            variants={scaleIn}
            whileHover={{ y: -5 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.desc}</p>
              </div>
              <div className={`text-2xl bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.label === 'Total Posts' && '📝'}
                {stat.label === 'Published' && '✅'}
                {stat.label === 'Featured' && '⭐'}
                {stat.label === 'Authors' && '👨‍⚕️'}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Tabs Navigation */}
      <motion.div
        variants={fadeInUp}
        className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-8"
      >
        <div className="flex border-b border-gray-200">
          {[
            { id: 'posts', label: 'Blog Posts', icon: '📝', count: stats.totalPosts },
            { id: 'authors', label: 'Authors', icon: '👨‍⚕️', count: stats.totalAuthors },
            { id: 'categories', label: 'Categories', icon: '🏷️', count: stats.totalCategories },
            { id: 'preview', label: 'Live Preview', icon: '👁️', count: null }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-4 text-center font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-teal-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <span className="text-xl">{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-sm">
                    {tab.count}
                  </span>
                )}
              </div>
              {activeTab === tab.id && (
                <motion.div
                  layoutId="tabIndicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-500"
                />
              )}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Side - List/Table */}
        <div className="lg:col-span-2">
          {activeTab === 'posts' && (
            <motion.div
              key="posts"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Filters */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search posts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                    />
                  </div>
                  
                  <div>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                    >
                      <option value="all">All Status</option>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setIsCreating(true);
                      setIsEditing(false);
                      setSelectedPost(null);
                    }}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow flex items-center justify-center space-x-2"
                  >
                    <FaPlus />
                    <span>Create New Post</span>
                  </motion.button>
                </div>
              </div>

              {/* Posts List */}
              <div className="space-y-4">
                {filteredPosts.map((post) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -4 }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Image */}
                        <div className="md:w-1/4">
                          <div className="relative h-40 rounded-xl overflow-hidden">
                            {post.image ? (
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-r from-gray-200 to-gray-300 flex items-center justify-center">
                                <FaImage className="text-gray-400 text-3xl" />
                              </div>
                            )}
                            <div className="absolute top-2 right-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                post.status === 'published' 
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {post.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="md:w-3/4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="text-lg font-bold text-gray-800 mb-1">
                                {post.title}
                              </h3>
                              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2">
                                <span className="flex items-center space-x-1">
                                  <FaCalendarAlt className="text-xs" />
                                  <span>{formatDate(post.date)}</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <FaUser className="text-xs" />
                                  <span>{post.author}</span>
                                </span>
                                <span className="px-2 py-1 bg-gray-100 rounded-full capitalize">
                                  {post.category}
                                </span>
                                {post.featured && (
                                  <span className="px-2 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 rounded-full">
                                    Featured
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <p className="text-gray-600 mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>

                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.tags?.map((tag) => (
                              <span
                                key={tag}
                                className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>

                          {/* Stats and Actions */}
                          <div className="flex flex-wrap justify-between items-center">
                            <div className="flex items-center space-x-4 text-gray-600">
                              <div className="flex items-center space-x-1">
                                <span>👁️ {post.views || 0}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <span>❤️ {post.likes || 0}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <span>💬 {post.comments || 0}</span>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 mt-4 md:mt-0">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleEditPost(post)}
                                className="px-4 py-2 bg-teal-100 text-teal-600 rounded-xl font-medium hover:bg-teal-200 transition-colors"
                              >
                                <FaEdit className="inline mr-2" />
                                Edit
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleDeletePost(post.id)}
                                className="px-4 py-2 bg-red-100 text-red-600 rounded-xl font-medium hover:bg-red-200 transition-colors"
                              >
                                <FaTrash className="inline mr-2" />
                                Delete
                              </motion.button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'authors' && (
            <motion.div
              key="authors"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Authors Header */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-800">Manage Authors</h3>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedAuthor({})}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-medium flex items-center space-x-2"
                  >
                    <FaPlus />
                    <span>Add Author</span>
                  </motion.button>
                </div>
              </div>

              {/* Authors List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {blogStore.authors.map((author) => (
                  <motion.div
                    key={author.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ y: -4 }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 rounded-full border-2 border-teal-200 overflow-hidden bg-gray-100">
                        {author.image ? (
                          <img
                            src={author.image}
                            alt={author.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-full h-full flex items-center justify-center bg-gray-100"
                          style={{ display: author.image ? 'none' : 'flex' }}
                        >
                          <FaUser className="text-gray-400 text-2xl" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800">{author.name}</h4>
                        <p className="text-gray-600 text-sm">{author.role}</p>
                        {author.bio && (
                          <p className="text-gray-500 text-sm mt-2 line-clamp-2">{author.bio}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedAuthor(author)}
                          className="p-2 text-teal-600 hover:text-teal-700"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this author?')) {
                              blogStore.deleteAuthor(author.id).catch(console.error);
                            }
                          }}
                          className="p-2 text-red-600 hover:text-red-700"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'categories' && (
            <motion.div
              key="categories"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Categories Header */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-800">Manage Categories</h3>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory({})}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-medium flex items-center space-x-2"
                  >
                    <FaPlus />
                    <span>Add Category</span>
                  </motion.button>
                </div>
              </div>

              {/* Categories List */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {blogStore.categories.map((category) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    className={`bg-gradient-to-r ${category.color} rounded-2xl shadow-lg p-6 text-white`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-lg">{category.name}</h4>
                        {category.description && (
                          <p className="text-white/80 text-sm mt-2">{category.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedCategory(category)}
                          className="p-1 text-white hover:text-white/80"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this category?')) {
                              await blogStore.deleteCategory(category.id).catch(console.error);
                            }
                          }}
                          className="p-1 text-white hover:text-white/80"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 text-sm">
                      {blogStore.blogPosts.filter(post => post.category === category.id).length} posts
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-6">Live Preview</h3>
              <p className="text-gray-600 mb-4">
                This is how your blog posts will appear on the main blog page:
              </p>
              
              <div className="space-y-6">
                {blogStore.blogPosts
                  .filter(post => post.status === 'published')
                  .slice(0, 3)
                  .map((post) => (
                    <div key={post.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        {post.image && (
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <h4 className="font-bold text-gray-800">{post.title}</h4>
                          <p className="text-sm text-gray-600">{post.author}</p>
                        </div>
                      </div>
                      <p className="text-gray-700">{post.excerpt}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{post.category}</span>
                          <span>{post.readTime}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatDate(post.date)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl">
                <p className="text-teal-700 font-medium">
                  💡 Any changes you make here will immediately reflect on the main blog page!
                </p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Right Side - Forms/Details */}
        <div className="space-y-6">
          <AnimatePresence>
            {/* Post Form */}
            {(isCreating || isEditing) && (
              <motion.div
                key="post-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">
                    {isEditing ? 'Edit Post' : 'Create New Post'}
                  </h3>
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setIsEditing(false);
                      setSelectedPost(null);
                      resetPostForm();
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                  {/* Form fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={isEditing ? selectedPost?.title : postForm.title}
                      onChange={(e) => {
                        if (isEditing) {
                          setSelectedPost({ ...selectedPost, title: e.target.value });
                        } else {
                          setPostForm({ ...postForm, title: e.target.value });
                        }
                      }}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="Enter post title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt *
                    </label>
                    <textarea
                      value={isEditing ? selectedPost?.excerpt : postForm.excerpt}
                      onChange={(e) => {
                        if (isEditing) {
                          setSelectedPost({ ...selectedPost, excerpt: e.target.value });
                        } else {
                          setPostForm({ ...postForm, excerpt: e.target.value });
                        }
                      }}
                      rows="3"
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="Enter brief excerpt"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Author *
                      </label>
                      <select
                        value={isEditing ? selectedPost?.authorId : postForm.authorId}
                        onChange={(e) => {
                          if (isEditing) {
                            setSelectedPost({ ...selectedPost, authorId: e.target.value });
                          } else {
                            setPostForm({ ...postForm, authorId: e.target.value });
                          }
                        }}
                        className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      >
                        <option value="">Select Author</option>
                        {blogStore.authors.map(author => (
                          <option key={author.id} value={author.id}>
                            {author.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={isEditing ? selectedPost?.category : postForm.category}
                        onChange={(e) => {
                          if (isEditing) {
                            setSelectedPost({ ...selectedPost, category: e.target.value });
                          } else {
                            setPostForm({ ...postForm, category: e.target.value });
                          }
                        }}
                        className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      >
                        {blogStore.categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Read Time
                      </label>
                      <input
                        type="text"
                        value={isEditing ? selectedPost?.readTime : postForm.readTime}
                        onChange={(e) => {
                          if (isEditing) {
                            setSelectedPost({ ...selectedPost, readTime: e.target.value });
                          } else {
                            setPostForm({ ...postForm, readTime: e.target.value });
                          }
                        }}
                        className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                        placeholder="e.g., 5 min read"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={isEditing ? selectedPost?.status : postForm.status}
                        onChange={(e) => {
                          if (isEditing) {
                            setSelectedPost({ ...selectedPost, status: e.target.value });
                          } else {
                            setPostForm({ ...postForm, status: e.target.value });
                          }
                        }}
                        className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image
                    </label>
                    <div className="space-y-3">
                      {/* Image Upload Input */}
                      <div className="flex items-center space-x-3">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleImageUpload}
                          className="flex-1 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                        />
                        {(imagePreview || (isEditing ? selectedPost?.image : postForm.image)) && (
                          <button
                            type="button"
                            onClick={removeImage}
                            className="px-3 py-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      
                      {/* Image Preview */}
                      {(imagePreview || (isEditing ? selectedPost?.image : postForm.image)) && (
                        <div className="relative">
                          <img
                            src={imagePreview || (isEditing ? selectedPost?.image : postForm.image)}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-xl border border-gray-200"
                          />
                          <div className="absolute top-2 right-2 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                            Image Selected
                          </div>
                        </div>
                      )}
                      
                      {/* Fallback URL Input */}
                      <div>
                        <input
                          type="text"
                          value={isEditing ? selectedPost?.image : postForm.image}
                          onChange={(e) => {
                            if (isEditing) {
                              setSelectedPost({ ...selectedPost, image: e.target.value });
                            } else {
                              setPostForm({ ...postForm, image: e.target.value });
                            }
                          }}
                          className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                          placeholder="Or enter image URL directly..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <div className="flex space-x-2 mb-3">
                      <input
                        type="text"
                        value={postForm.tagInput}
                        onChange={(e) => setPostForm({ ...postForm, tagInput: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                        className="flex-1 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                        placeholder="Add tag and press Enter"
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="px-4 bg-teal-100 text-teal-600 rounded-xl hover:bg-teal-200 transition-colors"
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(isEditing ? selectedPost?.tags : postForm.tags)?.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center space-x-1 bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm"
                        >
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="text-teal-500 hover:text-teal-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Featured Checkbox */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={isEditing ? selectedPost?.featured : postForm.featured}
                      onChange={(e) => {
                        if (isEditing) {
                          setSelectedPost({ ...selectedPost, featured: e.target.checked });
                        } else {
                          setPostForm({ ...postForm, featured: e.target.checked });
                        }
                      }}
                      className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <label className="text-gray-700">Feature this post</label>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={isEditing ? handleUpdatePost : handleCreatePost}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow"
                    >
                      {isEditing ? 'Update Post' : 'Create Post'}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Author Form */}
            {selectedAuthor && (
              <motion.div
                key="author-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">
                    {selectedAuthor.id ? 'Edit Author' : 'Add Author'}
                  </h3>
                  <button
                    onClick={() => setSelectedAuthor(null)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={selectedAuthor.name || ''}
                      onChange={(e) => setSelectedAuthor({ ...selectedAuthor, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="Dr. John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <input
                      type="text"
                      value={selectedAuthor.role || ''}
                      onChange={(e) => setSelectedAuthor({ ...selectedAuthor, role: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="Medical Specialist"
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image
                    </label>
                    <div className="space-y-3">
                      {/* Image Upload Input */}
                      <div className="flex items-center space-x-3">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleAuthorImageUpload}
                          className="flex-1 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                        />
                        {(authorImagePreview || selectedAuthor?.image) && (
                          <button
                            type="button"
                            onClick={removeAuthorImage}
                            className="px-3 py-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      
                      {/* Image Preview */}
                      {(authorImagePreview || selectedAuthor?.image) && (
                        <div className="relative">
                          <img
                            src={authorImagePreview || selectedAuthor?.image}
                            alt="Author Preview"
                            className="w-full h-48 object-cover rounded-xl border border-gray-200"
                          />
                          <div className="absolute top-2 right-2 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                            Image Selected
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bio
                    </label>
                    <textarea
                      value={selectedAuthor.bio || ''}
                      onChange={(e) => setSelectedAuthor({ ...selectedAuthor, bio: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="Brief biography..."
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={selectedAuthor.id ? handleUpdateAuthor : handleCreateAuthor}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow"
                    >
                      {selectedAuthor.id ? 'Update Author' : 'Add Author'}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Category Form */}
            {selectedCategory && (
              <motion.div
                key="category-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">
                    {selectedCategory.id ? 'Edit Category' : 'Add Category'}
                  </h3>
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={selectedCategory.name || ''}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="e.g., Mental Health"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={selectedCategory.description || ''}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, description: e.target.value })}
                      rows="2"
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                      placeholder="Brief description..."
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image
                    </label>
                    <div className="space-y-3">
                      {/* Image Upload Input */}
                      <div className="flex items-center space-x-3">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleCategoryImageUpload}
                          className="flex-1 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                        />
                        {(categoryImagePreview || selectedCategory?.image) && (
                          <button
                            type="button"
                            onClick={removeCategoryImage}
                            className="px-3 py-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      
                      {/* Image Preview */}
                      {(categoryImagePreview || selectedCategory?.image) && (
                        <div className="relative">
                          <img
                            src={categoryImagePreview || selectedCategory?.image}
                            alt="Category Preview"
                            className="w-full h-48 object-cover rounded-xl border border-gray-200"
                          />
                          <div className="absolute top-2 right-2 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                            Image Selected
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gradient Color
                    </label>
                    <select
                      value={selectedCategory.color || 'from-teal-500 to-emerald-500'}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, color: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                    >
                      <option value="from-teal-500 to-emerald-500">Teal to Emerald</option>
                      <option value="from-blue-500 to-cyan-500">Blue to Cyan</option>
                      <option value="from-purple-500 to-pink-500">Purple to Pink</option>
                      <option value="from-orange-500 to-yellow-500">Orange to Yellow</option>
                      <option value="from-red-500 to-pink-500">Red to Pink</option>
                      <option value="from-indigo-500 to-blue-500">Indigo to Blue</option>
                    </select>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={selectedCategory.id ? handleUpdateCategory : handleCreateCategory}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow"
                    >
                      {selectedCategory.id ? 'Update Category' : 'Add Category'}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Data Tools Modal */}
            {showImportExport && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800">Data Tools</h3>
                    <button
                      onClick={() => setShowImportExport(false)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Export */}
                    <div>
                      <h4 className="font-bold text-gray-700 mb-3">Export Data</h4>
                      <p className="text-gray-600 text-sm mb-4">
                        Download all blog data as a JSON file for backup or transfer.
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleExport}
                        className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow flex items-center justify-center space-x-2"
                      >
                        <FaCloudUploadAlt />
                        <span>Export All Data</span>
                      </motion.button>
                    </div>

                    {/* Import */}
                    <div>
                      <h4 className="font-bold text-gray-700 mb-3">Import Data</h4>
                      <p className="text-gray-600 text-sm mb-4">
                        Import data from a previous export. This will replace all current data.
                      </p>
                      <textarea
                        value={importData}
                        onChange={(e) => setImportData(e.target.value)}
                        rows="6"
                        className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none font-mono text-sm"
                        placeholder="Paste JSON data here..."
                      />
                      <div className="mt-3 flex space-x-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleImport}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow"
                        >
                          Import Data
                        </motion.button>
                        <button
                          onClick={() => setImportData('')}
                          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick Stats Panel */}
          {!isCreating && !isEditing && !selectedAuthor && !selectedCategory && (
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-6 text-white"
            >
              <h3 className="text-xl font-bold mb-6">Blog Insights</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>Total Views</span>
                  <span className="text-2xl font-bold">{stats.totalViews}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Total Likes</span>
                  <span className="text-2xl font-bold">{stats.totalLikes}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Engagement Rate</span>
                  <span className="text-2xl font-bold">
                    {stats.totalViews > 0 
                      ? ((stats.totalLikes / stats.totalViews) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-teal-400">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <FaCogs className="text-xl" />
                  </div>
                  <div>
                    <p className="font-semibold">Real-time Sync</p>
                    <p className="text-sm text-teal-100">
                      Changes appear instantly on the blog page
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Sync Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <FaCheckCircle className="text-green-500 text-xl" />
            </div>
            <div>
              <p className="font-semibold text-gray-800">Data Sync Active</p>
              <p className="text-sm text-gray-600">
                All changes are automatically saved and synced with the blog page
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Last Updated</p>
            <p className="font-semibold text-gray-800">
              {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Export AdminBlog component
export { AdminBlog };

export default AdminBlog;